
/*
 * Nettie's Bundle Builder v0.4
 *
 * - Dynamic metaobject selections
 * - No hardcoded SKUs or variant labels
 * - Variant adjustments calculated from Shopify prices
 * - Validates every required selection step
 * - Synchronizes Shopify form variant inputs
 * - Exposes selections and production mapping
 *
 * Cart submission is handled by bundle-purchase.js.
 */

(function () {
  'use strict';

  const VERSION = '0.4';

  function formatMoney(cents, currency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(cents / 100);
  }

  function initialize(root) {
    if (root.dataset.initialized === 'true') return;

    const rootId = root.getAttribute('id');

    if (!rootId) {
      console.error('[Netties Bundle] Builder ID missing.');
      return;
    }

    const dataId = rootId.replace(
      'netties-bundle-',
      'netties-bundle-data-'
    );

    const dataElement = document.getElementById(dataId);

    const requestedFormId = root.getAttribute('data-form-id');
    const form = requestedFormId
      ? document.getElementById(requestedFormId)
      : null;

    const selects = Array.from(
      root.querySelectorAll('[data-netties-bundle-select]')
    );

    const summary = root.querySelector(
      '[data-netties-bundle-summary]'
    );

    const status = root.querySelector(
      '[data-netties-bundle-status]'
    );

    if (!dataElement) {
      console.error('[Netties Bundle] Variant JSON missing.');
      return;
    }

    if (!form || form.tagName !== 'FORM') {
      console.error(
        '[Netties Bundle] Product form not found:',
        requestedFormId
      );
      return;
    }

    if (!selects.length || !summary || !status) {
      console.error(
        '[Netties Bundle] Required interface elements missing.'
      );
      return;
    }

    let data;

    try {
      data = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error(
        '[Netties Bundle] Invalid variant JSON.',
        error
      );
      return;
    }

    /*
     * Build the variant pricing map.
     *
     * The lowest Shopify variant price is the base.
     * Every other variant represents an adjustment.
     *
     * Each adjustment must map to exactly one variant.
     */

    const sourceVariants = Array.isArray(data.variants)
      ? data.variants
      : [];

    const prices = sourceVariants.map(variant =>
      Number(variant.price)
    );

    let configurationValid =
      prices.length > 0 &&
      prices.every(price =>
        Number.isInteger(price) && price >= 0
      );

    const basePrice = configurationValid
      ? Math.min(...prices)
      : null;

    const variants = new Map();

    if (configurationValid) {
      sourceVariants.forEach(variant => {
        const adjustment =
          Number(variant.price) - basePrice;

        if (
          !variant.id ||
          !Number.isInteger(adjustment) ||
          adjustment < 0 ||
          variants.has(adjustment)
        ) {
          configurationValid = false;
          return;
        }

        variants.set(adjustment, {
          id: String(variant.id),
          sku: variant.sku || '',
          title: variant.title || '',
          price: Number(variant.price),
          adjustment,
          available: variant.available === true
        });
      });
    }

    if (!variants.has(0)) {
      configurationValid = false;
    }

    if (!configurationValid) {
      console.error(
        '[Netties Bundle] Invalid variant pricing. ' +
        'Each adjustment must have one unique variant.'
      );
    }

    /*
     * IMPORTANT:
     * Use getAttribute('id') instead of form.id.
     *
     * Shopify's input named "id" can shadow the
     * native form.id property in some browsers.
     */

    const formId = form.getAttribute('id');

    /*
     * Find all variant inputs associated with
     * this specific Shopify product form.
     *
     * Includes inputs outside the form that use
     * the HTML "form" attribute.
     */

    function getVariantInputs() {
      return Array.from(
        document.querySelectorAll('input[name="id"]')
      ).filter(input => input.form === form);
    }

    /*
     * Read the customer's current selections.
     */

    function getSelections() {
      return selects.map(select => {
        const option = select.selectedOptions[0];

        if (!select.value || !option) {
          return null;
        }

        return {
          label: select.dataset.stepLabel || '',
          name: option.dataset.name || '',
          sku: option.dataset.sku || '',
          productId: option.dataset.productId || '',
          optionId: option.value,
          adjustment: Number(
            option.dataset.adjustment
          )
        };
      });
    }

    /*
     * Calculate the complete bundle state.
     */

    function getState() {
      const selections = getSelections();

      const completed = selections.filter(
        selection => selection !== null
      );

      const adjustment = completed.reduce(
        (total, item) => total + item.adjustment,
        0
      );

      const variant = Number.isInteger(adjustment)
        ? variants.get(adjustment) || null
        : null;

      const labels = selects.map(select =>
        (select.dataset.stepLabel || '').trim()
      );

      const labelsValid =
        labels.every(Boolean) &&
        new Set(labels).size === labels.length;

      const selectionsValid =
        completed.length === selects.length &&
        completed.every(item =>
          typeof item.name === 'string' &&
          item.name.trim().length > 0 &&
          typeof item.sku === 'string' &&
          item.sku.trim().length > 0 &&
          Boolean(item.productId) &&
          Boolean(item.optionId) &&
          Number.isInteger(item.adjustment) &&
          item.adjustment >= 0
        );

      const valid =
        configurationValid &&
        labelsValid &&
        selectionsValid &&
        variant !== null &&
        variant.available === true;

      return {
        valid,
        complete: selectionsValid,
        configurationValid,
        required: selects.length,
        selected: completed.length,
        selections: completed,
        adjustment,
        basePrice,
        variant,
        formId,
        productId: data.productId
      };
    }

    /*
     * Synchronize the selected Shopify variant.
     *
     * Only inputs belonging to this product form
     * are changed.
     */

    function syncVariant(state) {
      if (!state.valid || !state.variant) return;

      const nextId = String(state.variant.id);
      const inputs = getVariantInputs();

      if (!inputs.length) {
        console.error(
          '[Netties Bundle] No variant input found for form:',
          formId
        );
        return;
      }

      inputs.forEach(input => {
        if (input.value === nextId) return;

        input.value = nextId;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      });
    }

    /*
     * Refresh the interface and notify
     * the purchase controller.
     */

    function update() {
      const state = getState();

      root.dataset.bundleComplete = String(
        state.valid
      );

      root.dataset.totalAdjustment = String(
        state.adjustment
      );

      const remaining =
        state.required - state.selected;

      if (!state.configurationValid) {
        summary.textContent =
          'Combo pricing is not configured correctly.';

      } else if (remaining > 0) {
        summary.textContent =
          'Choose ' + remaining + ' more ' +
          (remaining === 1 ? 'option' : 'options') +
          ' to complete your combo.';

      } else if (!state.variant) {
        summary.textContent =
          'This combination is not currently available.';

      } else if (!state.variant.available) {
        summary.textContent =
          'This combo option is currently unavailable.';

      } else {
        summary.textContent = 'Your combo is complete!';
      }

      if (state.valid) {
        syncVariant(state);
      }

      document.dispatchEvent(
        new CustomEvent('netties:bundle-updated', {
          detail: {
            formId,
            state
          }
        })
      );
    }

    /*
     * Public API used by the purchase controller.
     */

    const api = {
      version: VERSION,

      getState,

      refresh: update,

      getProperties() {
        const state = getState();

        if (!state.valid) return null;

        const properties = {};

        state.selections.forEach(selection => {
          properties[selection.label] =
            selection.name;
        });

        properties['_Bundle Component SKUs'] =
          state.selections
            .map(item => item.sku)
            .join(',');

        properties['_Bundle Component IDs'] =
          state.selections
            .map(item => item.productId)
            .join(',');

        return properties;
      },

      setStatus(message) {
        status.textContent = message || '';
      }
    };

    root.dataset.initialized = 'true';

    window.nettiesBundleBuilder = api;

    selects.forEach(select => {
      select.addEventListener('change', update);
    });

    update();

    console.info(
      '[Netties Bundle] v' + VERSION + ' initialized',
      {
        basePrice,
        variantCount: variants.size,
        formId
      }
    );
  }

  function start() {
    document
      .querySelectorAll('[data-netties-bundle]')
      .forEach(root => {
        try {
          initialize(root);
        } catch (error) {
          console.error(
            '[Netties Bundle] Initialization failed:',
            error
          );
        }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    start
  );








/*
 * NETTIE'S BUNDLE — DYNAMIC BACK BUTTON RESET v2
 *
 * Clears ALL bundle selections when leaving and
 * returning to the product page.
 *
 * Supports any number of bundle steps.
 * Handles Chrome, Safari and mobile restoration timing.
 */

(function installBundleBackReset() {

  const STORAGE_PREFIX = 'netties-bundle-left:';

  let resetTimers = [];
  let returning = false;
  let customerInteracted = false;

  function getRoots() {
    return Array.from(
      document.querySelectorAll('[data-netties-bundle]')
    );
  }

  function getSelects(root) {
    return Array.from(
      root.querySelectorAll(
        '[data-netties-bundle-select]'
      )
    );
  }

  function storageKey() {
    return STORAGE_PREFIX + location.pathname;
  }

  function preventAutofill() {
    getRoots().forEach(root => {
      getSelects(root).forEach(select => {
        select.setAttribute('autocomplete', 'off');
      });
    });
  }

  /*
   * Reset the internal Shopify variant to
   * the lowest-price/base variant.
   */
  function resetVariant(root) {

    const dataId = root.id.replace(
      'netties-bundle-',
      'netties-bundle-data-'
    );

    const dataElement = document.getElementById(dataId);

    if (!dataElement) return;

    try {
      const data = JSON.parse(dataElement.textContent);

      const variants = data.variants || [];

      const baseVariant = variants.reduce(
        (lowest, variant) => {
          if (!lowest || variant.price < lowest.price) {
            return variant;
          }
          return lowest;
        },
        null
      );

      if (!baseVariant) return;

      const form = document.getElementById(
        root.dataset.formId
      );

      if (!form) return;

      document.querySelectorAll(
        'input[name="id"]'
      ).forEach(input => {

        if (input.form !== form) return;

        if (input.value !== String(baseVariant.id)) {
          input.value = String(baseVariant.id);

          input.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      });

    } catch (error) {
      console.warn(
        '[Netties Bundle] Variant reset warning:',
        error
      );
    }
  }

  /*
   * Reset EVERY configured step.
   *
   * No hardcoded first/second/third box.
   */
  function resetAllBundles() {

    getRoots().forEach(root => {

      getSelects(root).forEach(select => {
        select.value = '';
        select.selectedIndex = 0;
      });

      resetVariant(root);

    });

    // Clear the previous Add to Cart transaction.
    submitting = false;
    completed = false;

    restoreButtonText();
    setFeedback('');

    // Recalculate summary, validity and button state.
    window.nettiesBundleBuilder?.refresh?.();

    updateButton();
  }

  function cancelPendingResets() {
    resetTimers.forEach(clearTimeout);
    resetTimers = [];
  }

  /*
   * When leaving the page, clear everything
   * BEFORE the browser stores the page in BFCache.
   */
  window.addEventListener('pagehide', function () {

    if (!getRoots().length) return;

    try {
      sessionStorage.setItem(storageKey(), '1');
    } catch (error) {}

    cancelPendingResets();
    resetAllBundles();

  });

  /*
   * On Back navigation, clear again to catch
   * browser-restored form values.
   */
  window.addEventListener('pageshow', function (event) {

    if (!getRoots().length) return;

    let isBackNavigation = event.persisted;

    try {
      const navigation =
        performance.getEntriesByType('navigation')[0];

      isBackNavigation =
        isBackNavigation ||
        navigation?.type === 'back_forward' ||
        performance.navigation?.type === 2;

      if (sessionStorage.getItem(storageKey()) === '1') {
        isBackNavigation = true;
        sessionStorage.removeItem(storageKey());
      }

    } catch (error) {}

    if (!isBackNavigation) return;

    returning = true;
    customerInteracted = false;

    cancelPendingResets();
    preventAutofill();
    resetAllBundles();

    // Catch delayed restoration on mobile browsers.
    [50, 150, 350, 700, 1200, 2000].forEach(delay => {
      resetTimers.push(
        setTimeout(function () {
          if (!customerInteracted) {
            resetAllBundles();
          }
        }, delay)
      );
    });

  });

  /*
   * Once the customer starts selecting again,
   * do not erase their new choices.
   */
  function markCustomerInteraction(event) {
    if (!returning) return;

    const target = event.target;

    if (
      !target?.closest?.(
        '[data-netties-bundle-select]'
      )
    ) return;

    customerInteracted = true;
    returning = false;

    cancelPendingResets();
  }

  document.addEventListener(
    'pointerdown',
    markCustomerInteraction,
    true
  );

  document.addEventListener(
    'touchstart',
    markCustomerInteraction,
    true
  );

  document.addEventListener(
    'keydown',
    markCustomerInteraction,
    true
  );

  // Install autofill protection on the initial page.
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      preventAutofill,
      { once: true }
    );
  } else {
    preventAutofill();
  }

})();




})();
